import axios from "axios";
import Vue from "vue";
import actions from "../shared/actions";
import { ACCESS_TOKEN } from "../store/commonExport";

// window._CONFIG 由运行时注入（casPrefixUrl等），与main.ts/store中的用法保持一致
declare global {
  interface Window {
    _CONFIG: any;
  }
}

/** 刷新token接口（走网关匿名白名单） */
const REFRESH_URL = "/web/authmanage/auth/refreshToken";

/**
 * refresh_token存localStorage（跨标签页共享）：轮转后其他标签页能读到最新值，
 * 避免A页轮转导致B页手里的refresh_token变成已消费的死串而被踢去登录。
 * access token仍存sessionStorage（本标签页内主应用与子应用共享）。
 */
const RT_KEY = "refreshToken";

/** 单飞锁：并发多个请求同时401时，只发起一次刷新，其余等待同一Promise结果 */
let pendingRefresh: Promise<boolean> | null = null;

/**
 * 刷新专用裸axios实例：不挂主应用的401拦截器。
 * 刷新自身401时（如被其他标签页抢先轮转）先走"重读rt重试一次"的恢复逻辑，
 * 而不是被拦截器直接跳登录页。baseURL在调用时取全局默认值（模块加载时可能还没赋值）。
 */
const refreshHttp = axios.create({});

export function getAccessToken(): string | null {
  return sessionStorage.getItem("token");
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(RT_KEY);
}

export function clearTokens() {
  sessionStorage.removeItem("token");
  localStorage.removeItem(RT_KEY);
  localStorage.removeItem("Access-Token");
}

/** refresh_token也过期了：清空本地凭证，跳CAS重新登录 */
export function redirectToCasLogin() {
  clearTokens();
  const service = "http://" + window.location.host + "/";
  const serviceUrl = encodeURIComponent(service);
  window.location.href = window._CONFIG["casPrefixUrl"] + "/login?service=" + serviceUrl;
}

/** 发起一次刷新请求，成功则写回本地并推送微应用全局状态 */
function requestRefresh(refreshToken: string): Promise<boolean> {
  return refreshHttp
    .post(REFRESH_URL, { refreshToken }, { baseURL: axios.defaults.baseURL })
    .then((response) => {
      // 后端R.dealOKData结构：{totalElements, content:{token, refreshToken}, code:"0", msg:"SUCCESS"}
      const body = response.data || {};
      const content = body.content || {};
      if (body.code === "0" && content.token) {
        sessionStorage.setItem("token", content.token);
        if (content.refreshToken) {
          localStorage.setItem(RT_KEY, content.refreshToken);
        }
        // 同步旧版存储与微应用全局状态，保证子应用拿到新token
        (Vue as any).ls.set(ACCESS_TOKEN, content.token, 7 * 24 * 60 * 60 * 1000);
        actions.setGlobalState({ token: content.token });
        return true;
      }
      return false;
    })
    .catch(() => false);
}

/**
 * 单飞刷新access token：旧refresh_token换新token对（旧的立即作废）
 * 失败时若共享存储里的refresh_token已变化（其他标签页先轮转了），重读重试一次
 * @returns true=刷新成功且新token已写入本地；false=无refresh_token或刷新失败（应跳登录）
 */
export function refreshAccessToken(): Promise<boolean> {
  if (pendingRefresh) {
    return pendingRefresh;
  }
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return Promise.resolve(false);
  }
  pendingRefresh = requestRefresh(refreshToken)
    .then((ok) => {
      if (ok) {
        return true;
      }
      const latest = getRefreshToken();
      if (latest && latest !== refreshToken) {
        // 别的标签页已轮转，用最新refresh_token再试一次
        return requestRefresh(latest);
      }
      return false;
    })
    .then((ok) => {
      pendingRefresh = null;
      return ok;
    });
  return pendingRefresh;
}

/**
 * 401统一处理：先尝试单飞刷新，成功则用新token重放原请求；失败（含refresh_token过期）跳CAS重新登录
 * @param error axios错误对象（需含config）
 * @param http 发起重放的axios实例（主应用默认实例或apis独立实例）
 */
export function handleUnauthorized(error: any, http: any): Promise<any> {
  const config = error.config || {};
  // 刷新接口自身401：refresh_token也已过期，直接跳CAS重新登录
  if (config.url && config.url.indexOf("/authmanage/auth/refreshToken") !== -1) {
    redirectToCasLogin();
    return new Promise(() => {});
  }
  // 已重放过仍401：不再循环刷新，跳登录
  if (config.__retried401) {
    redirectToCasLogin();
    return new Promise(() => {});
  }
  config.__retried401 = true;
  return refreshAccessToken().then((ok) => {
    if (!ok) {
      redirectToCasLogin();
      return new Promise(() => {});
    }
    // 换新token重放原请求。重放时请求体已被axios序列化为字符串，
    // 必须保留/补回Content-Type，否则会按默认form-urlencoded发出导致后端415
    config.headers = Object.assign({}, config.headers, { ACCESS_TOKEN: getAccessToken() });
    if (typeof config.data === "string" && config.data
        && !config.headers["Content-Type"] && !config.headers["content-type"]) {
      config.headers["Content-Type"] = "application/json;charset=UTF-8";
    }
    return http.request(config);
  });
}

// ===== 会话保活：用户有操作且access临近过期时主动静默换新 =====
//
// 背景：刷新触发点只在主应用的401拦截器上，人在子应用里操作时子应用请求不经过它，
// token到期后子应用会持续401直到回主应用。子应用内的点击/键盘事件会冒泡到主应用
// document（qiankun同页面上下文），故在此监听"用户活动"：活跃且临期→主动刷新，
// sessionStorage/globalState随之更新，子应用按请求取token或监听globalState即可拿到新值。
// 完全闲置时不刷新，保持"闲置到期"的安全语义；回来后的操作会立即触发本检查自愈。

/** 活动检查节流间隔 */
const ACTIVITY_CHECK_INTERVAL_MS = 60 * 1000;
/** 距access过期多久开始主动刷新 */
const REFRESH_AHEAD_MS = 10 * 60 * 1000;

let lastActivityCheck = 0;

/** 解出JWT的exp（毫秒时间戳），解不出返回0 */
function getTokenExpMs(token: string): number {
  try {
    const part = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      window
        .atob(part)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    const claims = JSON.parse(json);
    return claims && typeof claims.exp === "number" ? claims.exp * 1000 : 0;
  } catch (e) {
    return 0;
  }
}

function maybeRefreshOnActivity() {
  const now = Date.now();
  if (now - lastActivityCheck < ACTIVITY_CHECK_INTERVAL_MS) {
    return;
  }
  lastActivityCheck = now;
  const token = getAccessToken();
  if (!token || !getRefreshToken()) {
    return;
  }
  const exp = getTokenExpMs(token);
  if (!exp) {
    return; // 历史token无exp，交由401流程处理
  }
  if (exp - now < REFRESH_AHEAD_MS) {
    refreshAccessToken().then(() => { /* 静默，失败由后续401流程兜底 */ }, () => {});
  }
}

/** 安装用户活动监听（main.ts启动时调用一次） */
export function installActivityRefresh() {
  ["mousedown", "keydown", "touchstart"].forEach((evt) =>
    document.addEventListener(evt, maybeRefreshOnActivity, { capture: true, passive: true })
  );
  // 切回标签页/窗口重新获得焦点时立即检查：闲置后回来的第一下操作往往就是业务请求，
  // 提前在这里把临期token换新，把"请求带着过期token发出去"的竞态窗口压到最小
  window.addEventListener("focus", maybeRefreshOnActivity);
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) {
      maybeRefreshOnActivity();
    }
  });
}
